import type { TryOnFineTuningSettingsType, TryOnSettings } from '@zakeke/zakeke-3d-core';
import { graphql, useLazyLoadQuery } from 'react-relay';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { TryOnProvider, TryOnType } from 'src/components/widgets/preview3D/tryOnTypes';
import useVTOStore from '../store';
import { useVTOProductTypeTryonProvidersQuery } from './__generated__/useVTOProductTypeTryonProvidersQuery.graphql';

// Il type è stato ridefinito perchè non posso usare il dynamic import qui.
export const tryOnTypeOptions = [
    { value: TryOnType.Eyewear, label: 'Eyewear', disabled: false },
    { value: TryOnType.Scarves, label: 'Scarves', disabled: false },
    { value: TryOnType.Earrings, label: 'Earrings', disabled: false },
    { value: TryOnType.Headwear, label: 'Headwear', disabled: false },
    { value: TryOnType.Necklace, label: 'Necklace', disabled: false },
    { value: TryOnType.Shoes, label: 'Shoes', disabled: false },
    { value: TryOnType.Watches, label: 'Watches', disabled: true },
    { value: TryOnType.Bracelet, label: 'Bracelet', disabled: true }
];

export const tryOnMazingFinetuningTemples = [
    { value: true, label: 'Visible' },
    { value: false, label: 'Hidden' }
];

export const tryOnMazingFinetuningEnvironments = [
    { value: 'neutral.hdr', label: 'Neutral' },
    { value: 'light.hdr', label: 'Light' },
    { value: 'bright.hdr', label: 'Bright' },
    { value: 'studio.hdr', label: 'Studio' },
    { value: 'court.hdr', label: 'Court' },
    { value: 'startup.hdr', label: 'Blurred Studio' }
];

const getReferenceData = async (type: TryOnType) => {
    const { DefaultAssetUrls } = await import('@zakeke/zakeke-3d-core');
    let fileUrl, downloadableUrl, name;

    switch (type) {
        case TryOnType.Eyewear:
            name = DefaultAssetUrls.TryonReferences.EyewearName;
            fileUrl = DefaultAssetUrls.TryonReferences.EyewearURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.EyewearDownloadableURL;
            break;
        case TryOnType.Scarves:
            name = DefaultAssetUrls.TryonReferences.ScarvesName;
            fileUrl = DefaultAssetUrls.TryonReferences.ScarvesURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.ScarvesDownloadableURL;
            break;
        case TryOnType.Earrings:
            name = DefaultAssetUrls.TryonReferences.EarringsName;
            fileUrl = DefaultAssetUrls.TryonReferences.EarringsURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.EarringsDownloadableURL;
            break;
        case TryOnType.Necklace:
            name = DefaultAssetUrls.TryonReferences.NecklaceName;
            fileUrl = DefaultAssetUrls.TryonReferences.NecklaceURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.NecklaceDownloadableURL;
            break;
        case TryOnType.Headwear:
            name = DefaultAssetUrls.TryonReferences.HeadwearName;
            fileUrl = DefaultAssetUrls.TryonReferences.HeadwearURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.HeadwearDownloadableURL;
            break;
        case TryOnType.Shoes:
            name = DefaultAssetUrls.TryonReferences.ShoeName;
            fileUrl = DefaultAssetUrls.TryonReferences.ShoeURL;
            downloadableUrl = DefaultAssetUrls.TryonReferences.ShoeDownloadableURL;
            break;
    }
    downloadableUrl = DefaultAssetUrls.TryonReferences.RootUrl + downloadableUrl;
    return { fileUrl, name, downloadableUrl };
};

const useVTOProductType = () => {
    const [zkProduct, sceneEditor, setAutoscale, setScaleValue, setPositionValue] = usePreview3DStore((x) => [
        x.zkProduct,
        x.sceneEditor,
        x.setAutoscale,
        x.setScaleValue,
        x.setPositionValue
    ]);
    const isDamMode = useVTOStore((x) => x.isDamMode);

    const showReferenceModel = async (type: TryOnType, forceReload: boolean) => {
        if (!sceneEditor || !zkProduct) return;
        const { fileUrl, name } = await getReferenceData(type);
        if (fileUrl && name) {
            if (forceReload) {
                sceneEditor.removeAllVTOReferenceMeshes();
                await sceneEditor.createTryOnReferenceModelsAsync(null, name, fileUrl);
            }

            sceneEditor.setVTOReferenceVisible(true);
        }
    };

    const changeProductType = async (type: TryOnType) => {
        if (!sceneEditor || !zkProduct) return;

        sceneEditor.removeAllVTOReferenceMeshes();
        sceneEditor.resetProviderFineTuningData();
        sceneEditor.setTryOnDefaultTypeSettings();
        sceneEditor.setTryOnSelectedType(type ?? TryOnType.NotSelected);

        // const id = showInfoNotification({
        // 	message: T._('Loading reference model...', 'Admin'),
        // 	dismiss: {
        // 		duration: 50000
        // 	}
        // });

        await showReferenceModel(type, true);

        setPositionValue(sceneEditor!.getScenePositionPercentageForVTO()!);
        setScaleValue(sceneEditor!.getSceneScalingPercentageForVTO()!);
        setAutoscale(true);

        if (type === TryOnType.Shoes) sceneEditor.scaleSceneToTryonReferenceModels('shoes');
        else sceneEditor.scaleSceneToTryonReferenceModels();
        sceneEditor.adjustCamera(false);

        // removeNotification(id);
        // showSuccessNotification({
        // 	message: T._('Reference model loaded', 'Admin')
        // });
    };

    const downloadReferenceModel = async (type: TryOnType) => {
        if (!sceneEditor || !zkProduct) return;

        const { downloadableUrl, name } = await getReferenceData(type);

        if (downloadableUrl && name) {
            const fileName = name + '.glb';
            // download as a file
            const link = document.createElement('a');
            link.href = downloadableUrl;
            link.target = '_blank';
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const getSelectedType = () => {
        if (!sceneEditor) return null;
        return sceneEditor.getTryOnSelectedType();
    };

    const getVTOSettings = () => {
        if (!sceneEditor) return null;
        return sceneEditor.getTryOnSettings();
    };

    const setVTOSettings = (settings: TryOnSettings) => {
        if (!sceneEditor) return;
        sceneEditor.setTryOnTypeSettings(settings);
    };

    const getSelectedProvider = (): TryOnProvider | null => {
        if (!sceneEditor) return null;
        const type = sceneEditor.getTryOnSelectedType() ?? TryOnType.NotSelected;
        if (type === TryOnType.NotSelected) return null;
        const tryOnProvidersQuery = useLazyLoadQuery<useVTOProductTypeTryonProvidersQuery>(
            graphql`
                query useVTOProductTypeTryonProvidersQuery {
                    tryOnProviders {
                        list {
                            key
                            value
                        }
                    }
                }
            `,
            {}
        );
        const providers = { list: tryOnProvidersQuery.tryOnProviders.list.map((x) => ({ key: x.key, value: x.value })) };
        const provider = providers.list.find((x) => x.key === type)?.value;
        return provider as TryOnProvider;
    };

    const getMeshBlob = async (): Promise<Blob | null> => {
        if (!sceneEditor) return null;
        const mesh = sceneEditor?.getTryOnMeshVisible() ?? null;
        if (!mesh) return null;
        return sceneEditor.exportMeshToGLBBlob(mesh.id);
    };

    const getMeshUrl = (): string | null => {
        if (!sceneEditor || !isDamMode) {
            return null;
        }

        // Per prodotti DAM, l'URL del GLB è settato direttamente in zkScene.url da loadDamSettings
        const zkScene = sceneEditor.zkScene;
        if (zkScene) {
            const url = zkScene.get('url');
            if (url && typeof url === 'string') {
                return url;
            }
        }

        return null;
    };

    const getFinetuningSettings = () => {
        console.log('🔍 getFinetuningSettings() called - isDamMode:', isDamMode, 'sceneEditor:', !!sceneEditor);
        if (!sceneEditor) return null;

        // Per prodotti DAM, controlla prima i frameDAMSettings
        if (isDamMode) {
            const zkScene = sceneEditor.zkScene;
            if (zkScene) {
                const frameDAMSettings = zkScene.get('config')?.get('general')?.get('frameDAMSettings');
                console.log('📋 frameDAMSettings (raw):', frameDAMSettings);
                if (frameDAMSettings && typeof frameDAMSettings === 'string') {
                    try {
                        const damSettingsObj = JSON.parse(frameDAMSettings);
                        console.log('📋 damSettingsObj (parsed):', damSettingsObj);
                        // Ora frameDAMSettings contiene solo { finetuning: ... } invece di tutti i settings VTO
                        if (damSettingsObj?.finetuning) {
                            console.log('✅ Found finetuning in frameDAMSettings:', damSettingsObj.finetuning);
                            return damSettingsObj.finetuning;
                        }
                    } catch (e) {
                        console.warn('Failed to parse frameDAMSettings:', e);
                    }
                }
            }
        }

        // Fallback ai metodi standard
        const dam_settings = sceneEditor.getDAMFineTuningData() ?? null;
        console.log('📋 dam_settings from getDAMFineTuningData():', dam_settings);
        if (dam_settings) return dam_settings;
        const mesh = sceneEditor?.getTryOnMeshVisible() ?? null;
        console.log('📋 mesh:', mesh);
        if (!mesh) {
            console.log('❌ No mesh found');
            return null;
        }
        const meshSettings = sceneEditor.getMeshFineTuningData(mesh);
        console.log('📋 meshSettings from getMeshFineTuningData():', meshSettings);
        return meshSettings;
    };

    const getDAMFinetuningSettings = () => {
        if (!sceneEditor) return null;
        return sceneEditor.getDAMFineTuningData();
    };

    const setFinetuningSettings = (settings: TryOnFineTuningSettingsType | null) => {
        console.log('💾 setFinetuningSettings() called with:', settings);
        if (!sceneEditor) {
            console.log('❌ No sceneEditor');
            return;
        }
        const mesh = sceneEditor?.getTryOnMeshVisible() ?? null;
        console.log('📋 mesh for setMeshFineTuningData:', mesh);
        if (!mesh) {
            console.log('❌ No mesh found');
            return;
        }
        sceneEditor.setMeshFineTuningData(mesh, settings as any);
        console.log('✅ setMeshFineTuningData() called');
    };

    const setDAMFinetuningSettings = (settings: TryOnFineTuningSettingsType | null) => {
        console.log('💾 setDAMFinetuningSettings() called with:', settings);
        if (!sceneEditor) {
            console.log('❌ No sceneEditor');
            return;
        }
        sceneEditor.setDAMFineTuningData(settings);
        console.log('✅ setDAMFineTuningData() called');
    };

    return {
        tryOnTypeOptions,
        getSelectedType,
        getSelectedProvider,
        changeProductType,
        downloadReferenceModel,
        getMeshBlob,
        getMeshUrl,
        showReferenceModel,
        getFinetuningSettings,
        setFinetuningSettings,
        getDAMFinetuningSettings,
        setDAMFinetuningSettings,
        getVTOSettings,
        setVTOSettings
    };
};

export default useVTOProductType;
